import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", {
        email,
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(formatApiErrorDetail(err.response?.data?.detail));
      } else {
        // Never reveal whether the email exists — show the same success either way.
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="w-full max-w-sm"
      >
        <button
          data-testid="forgot-back-button"
          onClick={() => navigate("/auth")}
          className="flex items-center gap-2 text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500 text-sm mb-12"
        >
          <ArrowLeft size={16} /> Sign in
        </button>

        {sent ? (
          <div data-testid="forgot-sent-message">
            <h1 className="font-serif-display font-light text-3xl sm:text-4xl tracking-tight">
              Check your email.
            </h1>
            <p className="mt-5 text-[var(--mirror-text-2)] leading-relaxed">
              If an account exists for that email, a reset link is on its way. It will stay open
              for one hour.
            </p>
            <Link
              to="/auth"
              className="mt-10 inline-block text-sm tracking-[0.15em] uppercase text-[var(--mirror-accent)] hover:opacity-80 transition-opacity duration-500"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h1 className="font-serif-display font-light text-3xl sm:text-4xl tracking-tight">
              Forgot your password?
            </h1>
            <p className="mt-5 text-[var(--mirror-text-2)] leading-relaxed">
              Enter your email and we'll send you a link to choose a new one.
            </p>

            <div className="mt-10">
              <label className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
                Email
              </label>
              <input
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-[rgba(242,236,228,0.15)] py-2 mt-2 outline-none focus:border-[var(--mirror-accent)] transition-colors duration-500 text-[var(--mirror-text)]"
              />
            </div>

            {error && (
              <p data-testid="forgot-error" className="mt-4 text-sm text-[#d98a8a]">
                {error}
              </p>
            )}

            <button
              data-testid="forgot-submit-button"
              type="submit"
              disabled={loading}
              className="mt-10 w-full py-3 rounded-full bg-[var(--mirror-accent)] text-[#16151F] text-sm tracking-[0.2em] uppercase font-medium hover:opacity-90 transition-opacity duration-500 disabled:opacity-50"
            >
              {loading ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
